import { useState, useMemo, useEffect, useCallback } from "react";
import { useLocation } from "react-router";

import {
  useFavouriteItemsQuery,
  useDeleteFavouriteMutation,
  useAddFavouriteMutation,
  useUpdateFavouriteMutation,
} from "../../../../favourites/api";
import { isEqual } from "../../../../favourites/utils";
import { FavouriteSlideItem, FavouriteSlideItemDraft, FavouriteItemType } from "../../../../favourites/types";

import {
  IFavouriteProviderContextValue,
  IFavouriteProviderContextActions,
  IFavouriteProviderContextState,
  IFavouriteProviderProps,
} from "./FavouriteProvider.types";

export function useFavouriteState(props: IFavouriteProviderProps): IFavouriteProviderContextValue {
  const { search } = useLocation();
  const hasFavouriteParam = new URLSearchParams(search).get("favourite") !== null;

  const { reportId, applySavedFavouriteByDefault = hasFavouriteParam } = props;

  const { data: favouriteItems = [], isLoading: isFavouriteItemsLoading } = useFavouriteItemsQuery();
  const { mutate: updateFavouriteMutation } = useUpdateFavouriteMutation();
  const { mutate: addFavouriteMutation } = useAddFavouriteMutation();
  const { mutate: deleteFavouriteMutation } = useDeleteFavouriteMutation();

  const [currentFavouriteItem, setCurrentFavouriteItem] = useState<FavouriteSlideItemDraft | undefined>();
  const [applyFunction, setApplyFunction] = useState<(parameters: Record<string, string[]>) => void | undefined>();

  const savedFavouriteItem = useMemo<FavouriteSlideItem | undefined>(() => {
    return favouriteItems.find((item) => item.type === FavouriteItemType.Slide && item.mark.reportId === reportId) as FavouriteSlideItem;
  }, [favouriteItems, reportId]);

  const setParameters = useCallback(
    (name: string, parameters: Record<string, (number | string)[] | number | null | undefined>) => {
      if (!name || Object.values(parameters).some((v) => v === null || v === undefined)) {
        setCurrentFavouriteItem(undefined);
      } else {
        setCurrentFavouriteItem({
          type: FavouriteItemType.Slide,
          name,
          mark: {
            reportId,
            parameters: Object.entries(parameters).reduce(
              (acc, [paramId, paramValue]) => ({ ...acc, [paramId]: (Array.isArray(paramValue) ? paramValue : [paramValue]).map(String) }),
              {}
            ),
          },
        });
      }
    },
    [reportId]
  );

  const addFavourite = useCallback(() => {
    if (currentFavouriteItem) {
      addFavouriteMutation(currentFavouriteItem);
    }
  }, [addFavouriteMutation, currentFavouriteItem]);

  const deleteFavourite = useCallback(() => {
    if (savedFavouriteItem) {
      deleteFavouriteMutation(savedFavouriteItem);
    }
  }, [deleteFavouriteMutation, savedFavouriteItem]);

  const updateFavourite = useCallback(() => {
    if (savedFavouriteItem && currentFavouriteItem) {
      updateFavouriteMutation({ oldFavouriteItem: savedFavouriteItem, newFavouriteItem: currentFavouriteItem });
    }
  }, [updateFavouriteMutation, savedFavouriteItem, currentFavouriteItem]);

  const cancelFavouriteChanges = useCallback(() => {
    if (savedFavouriteItem && applyFunction) {
      applyFunction(savedFavouriteItem.mark.parameters);
    }
  }, [savedFavouriteItem, applyFunction]);

  useEffect(() => {
    if (savedFavouriteItem && applyFunction && applySavedFavouriteByDefault) {
      applyFunction(savedFavouriteItem.mark.parameters);
    }
  }, [savedFavouriteItem, applyFunction, applySavedFavouriteByDefault]);

  const state = useMemo<IFavouriteProviderContextState>(
    () => ({
      currentFavouriteItem,
      savedFavouriteItem,
      isFavouriteLoading: isFavouriteItemsLoading,
      isFavouriteDisabled: !currentFavouriteItem,
      isFavouriteChanged: !!savedFavouriteItem && !!currentFavouriteItem && !isEqual(savedFavouriteItem, currentFavouriteItem),
    }),
    [savedFavouriteItem, isFavouriteItemsLoading, currentFavouriteItem]
  );

  const actions = useMemo<IFavouriteProviderContextActions>(
    () => ({
      addFavourite,
      deleteFavourite,
      updateFavourite,
      setParameters,
      setApplyFunction: (func) => setApplyFunction(() => func),
      cancelFavouriteChanges,
    }),
    [addFavourite, deleteFavourite, updateFavourite, setParameters, setApplyFunction, cancelFavouriteChanges]
  );

  const value = useMemo(() => ({ state, actions }), [actions, state]);

  return value;
}
